/* RESPONSIBLE TEAM: team-ai-insights */

import type AppService from 'embercom/services/app-service';
import moment from 'moment-timezone';
import type { ProjectedConversationsRanking } from 'embercom/lib/suggestions/constants';
import {
  type OptimizeSuggestionFilterState,
  type OptimizeSuggestionItem,
  type OptimizeSuggestionType,
} from 'embercom/components/reporting/automation/ai-insights/optimize-suggestions/lib/constants';

export function applyFilters(
  appService: AppService,
  suggestions: OptimizeSuggestionItem[],
  filterState: OptimizeSuggestionFilterState,
): OptimizeSuggestionItem[] {
  let filteredSuggestions: OptimizeSuggestionItem[] = suggestions;
  let typeFilters = filterState.suggestionTypes ?? [];
  let dateFilter = filterState.date;
  let topicFilters = filterState.topics ?? [];
  let impactFilters = filterState.impactFilter ?? [];

  if (typeFilters.length > 0) {
    filteredSuggestions = filterBySuggestionType(filteredSuggestions, typeFilters);
  }
  if (dateFilter) {
    filteredSuggestions = filterByRange(appService, filteredSuggestions, dateFilter);
  }
  if (topicFilters.length > 0) {
    filteredSuggestions = filterByTopics(filteredSuggestions, topicFilters);
  }
  if (impactFilters.length > 0) {
    filteredSuggestions = filterByImpact(filteredSuggestions, impactFilters);
  }
  return filteredSuggestions;
}

function filterBySuggestionType(
  suggestions: OptimizeSuggestionItem[],
  suggestionTypes: OptimizeSuggestionType[],
): OptimizeSuggestionItem[] {
  return suggestions.filter((suggestion) => suggestionTypes.includes(suggestion.suggestionType));
}

function filterByRange(
  appService: AppService,
  suggestions: OptimizeSuggestionItem[],
  date: string,
): OptimizeSuggestionItem[] {
  let startDate = moment.tz(date, appService.app.timezone).startOf('week').startOf('day');
  let endOfDate = moment.tz(date, appService.app.timezone).endOf('week').endOf('day');
  return suggestions.filter((suggestion) => {
    let suggestionDate = moment.tz(suggestion.createdAt, appService.app.timezone);
    return suggestionDate >= startDate && suggestionDate <= endOfDate;
  });
}

function filterByTopics(
  suggestions: OptimizeSuggestionItem[],
  topics: string[],
): OptimizeSuggestionItem[] {
  return suggestions.filter((suggestion) =>
    suggestion.topicList.some((topic) => topics.includes(topic)),
  );
}

function filterByImpact(
  suggestions: OptimizeSuggestionItem[],
  impactLevels: ProjectedConversationsRanking[],
): OptimizeSuggestionItem[] {
  return suggestions.filter((suggestion) => impactLevels.includes(suggestion.impactRanking));
}
